'use client';

import React, { useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { OrganizerSidebar } from '@/components/layout/OrganizerSidebar';
import { ParticipantSidebar } from '@/components/layout/ParticipantSidebar';
import { JurySidebar } from '@/components/layout/JurySidebar';
import { OrganizationSidebar } from '@/components/layout/OrganizationSidebar';
import { EventWorkspaceSidebar } from '@/components/layout/EventWorkspaceSidebar';
import { ActivityWorkspaceSidebar } from '@/components/layout/ActivityWorkspaceSidebar';
import { DashboardShell } from '@/components/layout/DashboardShell';
import { DashboardNavigationLayer } from '@/components/layout/DashboardNavigationLayer';
import { EventWorkspaceProvider } from '@/components/event-workspace/event-workspace-context';
import { ActivityFrameworkWorkspaceProvider } from '@/components/event-workspace/ActivityFrameworkWorkspaceProvider';
import { AdminBadgesProvider } from '@/contexts/AdminBadgesContext';
import { authDebugEnabled } from '@/lib/auth-debug';
import { resolveDashboardAuth } from '@/lib/dashboard-auth';
import { resolveRoleZoneRedirect } from '@/lib/role-dashboard';
import { safeReplace } from '@/lib/safe-redirect';
import { traceLogin } from '@/lib/login-redirect-trace';
import { useAuthStore } from '@/lib/auth-store';
import { getEffectiveRole, isAdminRole, isJuryRole, isOrganizerRole, isOrganizationStaffRole } from '@/lib/rbac';
import { canAccessOrganizationDashboard } from '@/lib/organization-access';
import { isActivityWorkspacePath, useResolvedEventId } from '@/lib/resolve-static-param';
import { EnthiosHubLoader } from '@/components/ui/EnthiosHubLoader';
import { maintenanceApi } from '@/lib/api';
import { NewOrganizationAlertModal } from '@/components/admin/NewOrganizationAlertModal';

function DashboardAuthGate({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const { user, authChecking, _hasHydrated } = useAuthStore();

  const auth = resolveDashboardAuth(pathname, user, {
    hydrated: _hasHydrated,
    authChecking,
  });
  const authStatus = auth.status;
  const authLoginUrl = auth.status === 'unauthenticated' ? auth.loginUrl : null;

  useEffect(() => {
    if (!_hasHydrated || authChecking) return;

    if (authStatus === 'unauthenticated' && authLoginUrl) {
      traceLogin('GUARD REDIRECT TO LOGIN', {
        source: 'DashboardAuthGate',
        pathname,
        destination: authLoginUrl,
        isAuthenticated: false,
        hasUser: Boolean(user),
        authChecking,
      });
      router.replace(authLoginUrl);
      return;
    }

    // Check role-zone redirection (e.g. /dashboard -> /dashboard/organization) before treating as forbidden
    const zoneRedirect = resolveRoleZoneRedirect(pathname, user);
    if (zoneRedirect) {
      if (authDebugEnabled()) {
        console.info('layout_guard', {
          pathname,
          userRole: getEffectiveRole(user),
          destination: zoneRedirect,
        });
      }
      traceLogin('GUARD ZONE REDIRECT', {
        source: 'DashboardAuthGate',
        pathname,
        destination: zoneRedirect,
        role: getEffectiveRole(user),
      });
      safeReplace(router, pathname, zoneRedirect);
      return;
    }

    if (authStatus === 'forbidden') {
      traceLogin('GUARD REDIRECT TO FORBIDDEN', {
        source: 'DashboardAuthGate',
        pathname,
        destination: '/403',
        role: getEffectiveRole(user),
      });
      router.replace('/403');
      return;
    }

    if (authStatus !== 'allowed') return;

    // Maintenance mode check: non-admin users cannot access any dashboard during active maintenance
    const currentRole = getEffectiveRole(user);
    const isAdmin = isAdminRole(currentRole);
    const isAdminPath = pathname.startsWith('/admin') || pathname.startsWith('/dashboard/admin');
    if (!isAdmin && !isAdminPath) {
      maintenanceApi
        .status()
        .then((res) => {
          if (res.data?.enabled) {
            window.location.href = '/maintenance';
          }
        })
        .catch(() => {});
    }
  }, [_hasHydrated, authChecking, authStatus, authLoginUrl, pathname, router, user]);

  if (!_hasHydrated) {
    return <EnthiosHubLoader variant="fullscreen" showDelayMs={0} />;
  }

  // A persisted user can render the shell while checkAuth revalidates.
  // Blocking the whole dashboard on authChecking is what left organization
  // home stuck on the branded loader after a cookie-session refresh.
  if ((authChecking || auth.status === 'loading') && !user) {
    return <EnthiosHubLoader variant="fullscreen" showDelayMs={0} />;
  }

  if (auth.status === 'unauthenticated' || auth.status === 'forbidden') {
    return (
      <EnthiosHubLoader
        variant="fullscreen"
        message="Redirecting…"
        showDelayMs={0}
        rotateMessages={false}
      />
    );
  }

  return <>{children}</>;
}

export default function SystemLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const resolvedEventId = useResolvedEventId();
  const { user } = useAuthStore();
  const effectiveRole = getEffectiveRole(user);

  if (!pathname) return null;

  const pathParts = pathname.split('/').filter(Boolean);
  const roleShellClass = isAdminRole(effectiveRole)
    ? 'bg-bg'
    : 'bg-[var(--color-bg)]';

  const isEventCreateWorkspace =
    pathParts[0] === 'dashboard' && pathParts[1] === 'events' && pathParts[2] === 'new';
  const isEventWorkspace =
    pathParts[0] === 'dashboard' && pathParts[1] === 'events' && pathParts[2] && pathParts[2] !== 'new';
  const eventId = isEventWorkspace ? resolvedEventId : '';
  const isEventWizardEdit = Boolean(pathname?.includes('/edit'));
  const isEventWizardShell = isEventWizardEdit || isEventCreateWorkspace;
  const isActivityWorkspace = isActivityWorkspacePath(pathname);
  const onJuryPath = pathParts[0] === 'jury';
  const workspaceShellClass = isActivityWorkspace ? 'bg-[var(--color-bg)]' : roleShellClass;

  const onOrganizationPath =
    pathParts[0] === 'dashboard' && pathParts[1] === 'organization';
  const onOrganizerPath =
    pathParts[0] === 'dashboard' && pathParts[1] === 'organizer';

  const useOrganizationSidebar =
    !isEventWorkspace &&
    !onOrganizerPath &&
    (onOrganizationPath
      ? canAccessOrganizationDashboard(user)
      : isOrganizationStaffRole(effectiveRole) && canAccessOrganizationDashboard(user));
  const isJuryShell =
    (onJuryPath || isJuryRole(effectiveRole)) &&
    !useOrganizationSidebar &&
    !isEventWorkspace &&
    !isEventCreateWorkspace;
  const isParticipantShell =
    !onJuryPath &&
    !isAdminRole(effectiveRole) &&
    !isOrganizerRole(effectiveRole) &&
    !isJuryRole(effectiveRole) &&
    !useOrganizationSidebar &&
    !isEventWorkspace &&
    !isEventCreateWorkspace;

  const mainContentPadding = isEventWizardShell
    ? 'px-3 sm:px-5 lg:px-6 pt-3 pb-24'
    : isParticipantShell || isJuryShell
      ? 'px-4 sm:px-6 lg:px-8 py-5'
      : 'p-4 sm:p-6 lg:p-8 pt-5';
  const SidebarComponent = useOrganizationSidebar
    ? OrganizationSidebar
    : isJuryShell
      ? JurySidebar
      : isParticipantShell
        ? ParticipantSidebar
        : OrganizerSidebar;

  return (
    <AdminBadgesProvider>
      <DashboardNavigationLayer />
      <NewOrganizationAlertModal />
      <DashboardAuthGate>
        {isEventWorkspace || isEventCreateWorkspace ? (
          <EventWorkspaceProvider eventId={eventId}>
            {isActivityWorkspace ? (
              <ActivityFrameworkWorkspaceProvider>
              <DashboardShell
                shellClassName={workspaceShellClass}
                mainPadding={mainContentPadding}
                sidebar={<ActivityWorkspaceSidebar />}
                showHeader={!isEventWizardShell}
                fullWidth={isEventWizardShell}
              >
                  {children}
                </DashboardShell>
              </ActivityFrameworkWorkspaceProvider>
            ) : (
              <DashboardShell
                shellClassName={roleShellClass}
                mainPadding={mainContentPadding}
                sidebar={<EventWorkspaceSidebar />}
                showHeader={!isEventWizardShell}
                fullWidth={isEventWizardShell}
              >
                {children}
              </DashboardShell>
            )}
          </EventWorkspaceProvider>
        ) : (
          <DashboardShell
            shellClassName={roleShellClass}
            mainPadding={mainContentPadding}
            sidebar={<SidebarComponent />}
            density={isJuryShell ? 'jury' : isParticipantShell ? 'participant' : 'default'}
          >
            {children}
          </DashboardShell>
        )}
      </DashboardAuthGate>
    </AdminBadgesProvider>
  );
}
